// The typed view of `window.__portalBoot` — the channel the inline classic
// script in index.html leaves behind on `/p/:token`. That script runs before
// the JS bundle: it fires `/boot`, preloads the operator's loading animation
// with an off-DOM `new Image()` (never animated by any engine, so the first
// paint is frame 0) and, when the bytes land, paints the loading screen itself
// by moving THAT element into an overlay appended to <body> outside #root.
// React (PortalPage) reads the channel instead of re-fetching, steps back
// while the overlay is up, and removes it here when the portal (or an error
// screen) first paints. src/__tests__/bootScript.test.ts pins the script's
// side of this contract; the harness in portalClientTestUtils.tsx mirrors it.

import type { PortalBootResponse } from "@/lib/api";

export interface PortalBootImage {
  url: string;
  state: "pending" | "loaded" | "failed";
  /** The preloaded element. ONE element from first paint to dismissal — a
   *  second `<img>` with the same src would restart the GIF. */
  el: HTMLImageElement;
  /** `Date.now()` when `src` was set — the spinner-cap clock (SPINNER_CAP_MS). */
  startedAt: number;
}

export interface PortalBootChannel {
  /** decodeURIComponent of the path segment after `/p/`. */
  token: string;
  /** `Date.now()` when /boot was issued. */
  startedAt: number;
  /** Resolves to the /boot JSON, or null on non-2xx / network. NEVER rejects. */
  boot: Promise<PortalBootResponse | null>;
  image: PortalBootImage | null;
  /** 0 until the overlay is in the DOM, then `Date.now()` of that moment. */
  paintedAt: number;
  /** React dismissed the screen → the script must never paint again. */
  taken: boolean;
  /** Bumped on every mutation; `useSyncExternalStore` reads it as the snapshot. */
  version: number;
  /** Notified synchronously after every bump. */
  listeners: Set<() => void>;
}

declare global {
  interface Window {
    __portalBoot?: PortalBootChannel;
  }
}

/** The overlay's element id — the script's markup and the harness use the same one. */
export const BOOT_SCREEN_ID = "portal-boot-screen";

/**
 * The channel for THIS token, or null: no inline script ran (tests, the legacy
 * `/portal/:token` route) or it ran for another token (a client-side
 * navigation — the portal has none today, but a stale channel must never be
 * read as this page's).
 */
export function getBootChannel(token: string): PortalBootChannel | null {
  const ch = typeof window === "undefined" ? undefined : window.__portalBoot;
  return ch && ch.token === token ? ch : null;
}

export function subscribeBootChannel(ch: PortalBootChannel, cb: () => void): () => void {
  ch.listeners.add(cb);
  return () => {
    ch.listeners.delete(cb);
  };
}

export function readBootVersion(ch: PortalBootChannel): number {
  return ch.version;
}

/**
 * The portal (or an error screen) is on screen: remove the script's overlay,
 * clear the page colour it wrote on body + html (PortalClient colours its
 * own wrapper) and mark the channel taken so a late `onload` paints nothing.
 * Idempotent — the second call bumps nothing — and safe without a channel.
 */
export function dismissBootScreen(): void {
  document.getElementById(BOOT_SCREEN_ID)?.remove();
  document.body.style.backgroundColor = "";
  document.documentElement.style.backgroundColor = "";
  const ch = window.__portalBoot;
  if (ch && !ch.taken) {
    ch.taken = true;
    ch.version++;
    ch.listeners.forEach((f) => f());
  }
}
